import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Grid,
  Rating,
  Chip,
  Button,
  TextField,
  Autocomplete,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Hotel,
  LocationOn,
  AttachMoney,
  Star as StarIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000';

const HotelCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  },
}));

const HotelImage = styled('img')({
  width: '100%',
  height: '200px',
  objectFit: 'cover',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

const PriceTag = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '8px 16px',
  borderRadius: '100px',
  background: theme.palette.primary.main,
  color: 'white',
  fontWeight: 600,
}));

const HotelSearch = ({ destination, dates, guests, onHotelSelect }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [destinationInfo, setDestinationInfo] = useState(null);
  const [hotels, setHotels] = useState([]);

  useEffect(() => {
    if (destination) {
      searchDestination(destination);
    }
  }, [destination]);

  useEffect(() => {
    if (destinationInfo && dates.arrival && dates.departure) {
      searchHotels();
    }
  }, [destinationInfo, dates, guests]);

  const searchDestination = async (query) => {
    try {
      setLoading(true);
      console.log('Searching destination:', query);
      const response = await axios.get(`${BACKEND_API_URL}/search-destination-airscraper`, {
        params: { query }
      });
      
      console.log('Destination search response:', response.data);
      if (response.data.status && response.data.data) {
        setDestinationInfo(response.data.data);
      }
    } catch (err) {
      console.error('Destination search error:', err);
      setError('Failed to search destination');
    } finally {
      setLoading(false);
    }
  };

  const searchHotels = async () => {
    if (!destinationInfo?.entityId || !dates?.arrival || !dates?.departure) return;

    try {
      setLoading(true);
      console.log('Searching hotels with params:', {
        entityId: destinationInfo.entityId,
        checkin: dates.arrival,
        checkout: dates.departure,
        adults: guests?.adults || 1,
        rooms: guests?.rooms || 1
      });

      const response = await axios.get(`${BACKEND_API_URL}/search-hotels-airscraper`, {
        params: {
          entityId: destinationInfo.entityId,
          checkin: dates.arrival,
          checkout: dates.departure,
          adults: guests?.adults || 1,
          rooms: guests?.rooms || 1,
          limit: 30,
          sorting: "-relevance",
          currency: "USD",
          market: "en-US",
          countryCode: "US"
        }
      });

      console.log('Hotel search response:', response.data);

      if (response.data.status && response.data.data?.hotels) {
        const hotels = response.data.data.hotels.filter(hotel => hotel && hotel.property);
        console.log('Filtered hotels:', hotels);
        setHotels(hotels);
      }
    } catch (err) {
      console.error('Hotel search error:', err);
      setError('Failed to search hotels');
    } finally {
      setLoading(false);
    }
  };

  // Calculate number of nights
  const calculateNights = () => {
    if (!dates.arrival || !dates.departure) return 0;
    const arrival = new Date(dates.arrival);
    const departure = new Date(dates.departure);
    return Math.ceil((departure - arrival) / (1000 * 60 * 60 * 24));
  };

  const nights = calculateNights();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {hotels.map((hotel) => (
          <Grid item xs={12} md={6} lg={4} key={hotel.hotel_id}>
            <HotelCard>
              <HotelImage
                src={hotel.property.photoUrls[0]}
                alt={hotel.property.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/400x300?text=No+Image+Available';
                }}
              />
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {hotel.property.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Rating
                      value={hotel.property.reviewRating || 0}
                      readOnly
                      precision={0.5}
                      icon={<StarIcon fontSize="small" />}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {hotel.property.reviewScoreWord} ({hotel.property.reviewCount} reviews)
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip 
                      size="small" 
                      label={`${hotel.property.propertyClass}-Star Hotel`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {hotel.property.wishlistName}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    {hotel.property.priceBreakdown.benefitBadges?.map((badge, index) => (
                      badge.text && (
                        <Chip
                          key={index}
                          size="small"
                          icon={<CheckIcon />}
                          label={badge.text}
                          color="primary"
                          variant="outlined"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      )
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
                    <PriceTag>
                      <AttachMoney />
                      ${hotel.property.priceBreakdown.grossPrice.value.toFixed(2)}
                      <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 400 }}>
                        /night
                      </Typography>
                    </PriceTag>
                    {hotel.property.priceBreakdown.totalPrice && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Total: ${hotel.property.priceBreakdown.totalPrice} for {nights} nights
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => onHotelSelect(hotel)}
                >
                  Select Hotel
                </Button>
              </CardContent>
            </HotelCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default HotelSearch; 